import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {FilteringWrapper} from '@pop-valet/smart-table-backend-wrapper';
import {AdminValetService} from '../../../../../admin/modules/admin-valet/services/admin-valet.service';
import {TranslateService} from '@ngx-translate/core';
import {GRID_ADD_ICON, GRID_DELETE_ICON, GRID_EDIT_ICON} from '../../../../../../environments/config';
import {Observable, Subject} from 'rxjs';
import {take, takeUntil, takeWhile} from 'rxjs/operators';
import {PvModalDialogService} from '../../../../../shared/services/pv-modal-dialog.service';

@Component({
  selector: 'pv-common-valet-withholding-grid',
  templateUrl: './common-valet-withholding-grid.component.html',
  styleUrls: ['./common-valet-withholding-grid.component.scss']
})
export class CommonValetWithholdingGridComponent extends FilteringWrapper implements OnInit, OnDestroy {

  @Input() valetId: string;
  @Input() invoicingUnblocked: Observable<void>;

  public currencyFormatter: Intl.NumberFormat;
  public availableRemunerationAmount: number | null = null;
  public availableExpenseAmount: number | null = null;
  public availableTotalAmount: number | null = null;

  private destroy$: Subject<void> = new Subject();

  constructor(private adminValetService: AdminValetService,
              private translateService: TranslateService,
              private pvModalService: PvModalDialogService) {
    super();
    this.currencyFormatter = new Intl.NumberFormat('fr-FR', {style: 'currency', currency: 'EUR'});
  }

  ngOnInit(): void {
    this.initGridCustomization();
    this.adminValetService.getAmountAvailableToWithhold(this.valetId).then(res => {
      this.availableRemunerationAmount = res.remuneration;
      this.availableExpenseAmount = res.expense;
      this.availableTotalAmount = res.remuneration + res.expense;
    });
    this.invoicingUnblocked.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.adminValetService.hasActiveWithholdings(this.valetId).then(hasActiveWithholdings => {
        if (!hasActiveWithholdings) {
          this.pvModalService.openValidationModal({
            title: this.translateService.instant('modules.valet.valet_profile.withholdings.invoicing_unblocked_modal.title'),
            message: this.translateService.instant('modules.valet.valet_profile.withholdings.invoicing_unblocked_modal.message'),
            showCancel: false
          }).pipe(
            takeWhile( (v) => !v.canceled && !v.backdropClicked),
            take(1)
          ).subscribe(() => {});
        }
      });
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
  }


  loadData(): void {
    this.loading = true;
    this.adminValetService.getWithholdings(this.valetId, this.filteringObj).then(res => {
      this.filteringObj.setPager(
        res.paginationData.currentPage,
        res.paginationData.pageCount,
        res.paginationData.perPage,
        res.paginationData.totalItems,
      );
      this.source.load(res.data);
      this.loading = false;
    }).catch(err => {
      this.loading = false;
    });
  }

  setColumns(): void {
    this.settings = {
      pager: false,
      mode: 'external',
      add: GRID_ADD_ICON,
      edit: GRID_EDIT_ICON,
      delete: GRID_DELETE_ICON,
      actions: {
        add: false,
        edit: true,
        delete: true,
        position: 'right'
      },
      hideSubHeader: true,
      columns: {
        name: {
          title: this.translateService.instant('modules.valet.valet_profile.withholdings.grid.name'),
          type: 'string',
          compareFunction: (cell, row) => {
          },
          filterFunction: (cell, row) => {
            return 1;
          }
        },
        totalAmount: {
          title: this.translateService.instant('modules.valet.valet_profile.withholdings.grid.total_amount'),
          type: 'string',
          compareFunction: (cell, row) => {
          },
          filterFunction: (cell, row) => {
            return 1;
          },
          valuePrepareFunction: (cell, row) => {
            return this.currencyFormatter.format(cell);
          }
        },
        remainingAmount: {
          title: this.translateService.instant('modules.valet.valet_profile.withholdings.grid.remaining_amount'),
          type: 'string',
          compareFunction: (cell, row) => {
          },
          filterFunction: (cell, row) => {
            return 1;
          },
          valuePrepareFunction: (cell, row) => {
            return this.currencyFormatter.format(cell);
          }
        },
        totalInstallments: {
          title: this.translateService.instant('modules.valet.valet_profile.withholdings.grid.total_installments'),
          type: 'string',
          compareFunction: (cell, row) => {
          },
          filterFunction: (cell, row) => {
            return 1;
          }
        },
        remainingInstallments: {
          title: this.translateService.instant('modules.valet.valet_profile.withholdings.grid.remaining_installments'),
          type: 'string',
          compareFunction: (cell, row) => {
          },
          filterFunction: (cell, row) => {
            return 1;
          }
        },
        allowInExpenses: {
          title: this.translateService.instant('modules.valet.valet_profile.withholdings.grid.allow_in_expenses'),
          type: 'string',
          compareFunction: (cell, row) => {
          },
          filterFunction: (cell, row) => {
            return 1;
          },
          valuePrepareFunction: (cell, row) => {
            return cell ? 'Oui' : 'Non';
          }
        }
      }
    };
  }

  createWithholding() {
    this.adminValetService.openWithholdingDialog(null).pipe(takeUntil(this.destroy$)).subscribe(res => {
      if (res.data) {
        this.adminValetService.createWithholding(this.valetId, res.data).then(() => {
          res.dialog.close();
          this.loadData();
        });
      } else {
        res.dialog.close();
      }
    });
  }

  updateWithholding(row: any) {
    this.adminValetService.openWithholdingDialog(row.data).pipe(takeUntil(this.destroy$)).subscribe(res => {
      if (res.data) {
        this.adminValetService.updateWithholding(this.valetId, row.data.id, res.data).then(() => {
          res.dialog.close();
          this.loadData();
        });
      } else {
        res.dialog.close();
      }
    });
  }

  deleteWithholding(row: any) {
    this.pvModalService.openValidationModal({
      message: this.translateService.instant('modules.valet.valet_profile.withholdings.grid.delete', {
        name: row.data.name
      })
    }).pipe(
      takeWhile( (v) => !v.canceled && !v.backdropClicked),
      take(1)
    ).subscribe(res => {
      if (res.confirmed) {
        this.adminValetService.deleteWithholding(this.valetId, row.data.id).then(() => {
          this.loadData();
        });
      }
    });
  }
}
